import {useEffect, useLayoutEffect, useRef, useState} from 'react';

const Y_AXIS_W  = 60;
const X_LABEL_H = 24;
const PAD_TOP   = 12;
const MIN_BAR_W   = 10;
const MAX_BAR_W   = 22;
const BAR_GAP     = 2;
const MONTH_GAP   = 10; // empty space between adjacent months' bar groups
const MIN_MONTH_W = MIN_BAR_W * 2 + BAR_GAP + MONTH_GAP + 2;
const PAD_RIGHT   = 10;
const MIN_LABEL_W = 34; // approx px width of "Jan 24" at 10 px font

function labelStep(monthW) {
    const raw = Math.ceil(MIN_LABEL_W * 1.2 / monthW);
    return [1, 2, 3, 6, 12].find(s => s >= raw) ?? 12;
}
const TIP_W = 280;
const TIP_H = 130;

function fmtAxis(v) {
    if (Math.abs(v) >= 1_000_000) return `${+(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000)     return `${Math.round(v / 1_000)}k`;
    return `${Math.round(v)}`;
}

function barTicks(max) {
    if (!max || max <= 0) return [0, 1];
    const rough = max / 4;
    const mag   = Math.pow(10, Math.floor(Math.log10(rough)));
    const step  = [1, 2, 2.5, 5, 10].map(c => c * mag).find(s => s >= rough) ?? mag * 10;
    const ticks = [0];
    // while loop guarantees last tick always >= max
    while (ticks[ticks.length - 1] < max && ticks.length <= 8)
        ticks.push(Math.round((ticks[ticks.length - 1] + step) * 1e9) / 1e9);
    return ticks;
}

function ChartTooltip({tip}) {
    if (!tip?.text) return null;
    const vw = window.innerWidth, vh = window.innerHeight;

    let style;

    const top = tip.y + 8 + TIP_H > vh ? Math.max(4, tip.y - TIP_H - 8) : tip.y + 8;

    if (tip.x > vw / 2) {
        style = {top, right: window.innerWidth - tip.x + 8}
    } else {
        style = {top, left: tip.x + 8}
    }

    return (
        <div className="tchart-tooltip" style={style}>
            {tip.month && <div className="tchart-tooltip__month">{tip.month}</div>}
            {tip.text.split('\n').map((line, i) => {
                if (!line) return null;
                if (line.endsWith(':'))        return <div key={i} className="tchart-tooltip__label">{line.slice(0, -1)}</div>;
                if (line.startsWith('Total:')) return <div key={i} className="tchart-tooltip__total">{line}</div>;
                return <div key={i} className="tchart-tooltip__value">{line}</div>;
            })}
        </div>
    );
}

const TimelineBarChart = ({data, label, height = 240}) => {
    const scrollRef = useRef(null);
    const [scrollW, setScrollW] = useState(0);
    const [tooltip, setTooltip] = useState(null);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el) setScrollW(Math.floor(el.getBoundingClientRect().width));
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => setScrollW(Math.floor(entries[0].contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (el) el.scrollLeft = el.scrollWidth;
        });
        return () => cancelAnimationFrame(id);
    }, [data]);

    const rows     = data.slice(1);
    const labels   = rows.map(r => r[0]);
    const incomes  = rows.map(r => Number(r[1]) || 0);
    const incTips  = rows.map(r => r[2] ?? '');
    const expenses = rows.map(r => Number(r[3]) || 0);
    const expTips  = rows.map(r => r[4] ?? '');

    const dataMax  = Math.max(...incomes, ...expenses, 1);
    const ticks    = barTicks(dataMax);
    const yMax     = ticks[ticks.length - 1];
    const SVG_H    = height + X_LABEL_H;
    const drawH    = height - PAD_TOP;
    const dataMinW = rows.length * MIN_MONTH_W + PAD_RIGHT;
    const svgW     = Math.max(scrollW, dataMinW);
    const monthW   = rows.length > 0 ? (svgW - PAD_RIGHT) / rows.length : MIN_MONTH_W;
    // Usable width for bars within each month slot (excludes the inter-month gap)
    const barSlot  = monthW - MONTH_GAP;
    const maxForSlot = Math.floor((barSlot - BAR_GAP) / 2);
    const barW     = Math.min(MAX_BAR_W, Math.max(MIN_BAR_W, Math.min(maxForSlot, Math.floor(barSlot * 0.45))));
    const barGroup = barW * 2 + BAR_GAP;
    const lblStep  = labelStep(monthW);

    const yScale  = v => PAD_TOP + (1 - v / yMax) * drawH;
    const baseY   = yScale(0);
    // Centre bars within barSlot, offset by half the inter-month gap
    const xOfBar  = i => i * monthW + MONTH_GAP / 2 + (barSlot - barGroup) / 2;
    const xOfLbl  = i => i * monthW + monthW / 2;

    const onHover = (e, text, month) => text && setTooltip({text, month, x: e.clientX, y: e.clientY});
    const onMove  = e => setTooltip(t => t ? {...t, x: e.clientX, y: e.clientY} : null);
    const onLeave = () => setTooltip(null);

    return (
        <div className="timeline-chart-section">
            <div className="tchart-header">
                <span className="timeline-chart-section__label">{label}</span>
                <div className="tchart-legend">
                    <span className="tchart-legend__item">
                        <span className="tchart-legend__sq" style={{background: 'var(--pos)'}}/>Income
                    </span>
                    <span className="tchart-legend__item">
                        <span className="tchart-legend__sq" style={{background: 'var(--neg)'}}/>Expense
                    </span>
                </div>
            </div>
            <div className="tchart-body">
                <svg width={Y_AXIS_W} height={SVG_H} className="tchart-yaxis">
                    {ticks.map(v => (
                        <text key={v} x={Y_AXIS_W - 6} y={yScale(v) + 4}
                              textAnchor="end" fontSize={10} fill="var(--text-muted)" fontFamily="inherit">
                            {fmtAxis(v)}
                        </text>
                    ))}
                </svg>
                <div className="timeline-chart-scroll" ref={scrollRef}>
                    <svg width={svgW} height={SVG_H} className="tchart-svg">
                        {ticks.slice(0, -1).map((v, i) => (
                            <line key={`m${i}`} x1={0} y1={yScale((v + ticks[i + 1]) / 2)} x2={svgW} y2={yScale((v + ticks[i + 1]) / 2)}
                                  stroke="var(--border)" strokeWidth={1} strokeOpacity={0.35}/>
                        ))}
                        {ticks.map(v => (
                            <line key={v} x1={0} y1={yScale(v)} x2={svgW} y2={yScale(v)}
                                  stroke="var(--border)" strokeWidth={1}/>
                        ))}
                        {rows.map((_, i) => {
                            const bx = xOfBar(i);
                            return (
                                <g key={i}>
                                    {incomes[i] > 0 && (
                                        <rect x={bx} y={yScale(incomes[i])}
                                              width={barW} height={baseY - yScale(incomes[i])}
                                              fill="var(--pos)" rx={2} className="tchart-bar"
                                              onMouseEnter={e => onHover(e, incTips[i], labels[i])}
                                              onMouseMove={onMove} onMouseLeave={onLeave}/>
                                    )}
                                    {expenses[i] > 0 && (
                                        <rect x={bx + barW + BAR_GAP} y={yScale(expenses[i])}
                                              width={barW} height={baseY - yScale(expenses[i])}
                                              fill="var(--neg)" rx={2} className="tchart-bar"
                                              onMouseEnter={e => onHover(e, expTips[i], labels[i])}
                                              onMouseMove={onMove} onMouseLeave={onLeave}/>
                                    )}
                                    {i % lblStep === 0 && (
                                        <text x={xOfLbl(i)} y={SVG_H - 6}
                                              textAnchor="middle" fontSize={10}
                                              fill="var(--text-muted)" fontFamily="inherit">
                                            {labels[i]}
                                        </text>
                                    )}
                                </g>
                            );
                        })}
                        <line x1={0} y1={baseY} x2={svgW} y2={baseY}
                              stroke="var(--border)" strokeWidth={1.5}/>
                    </svg>
                </div>
            </div>
            <ChartTooltip tip={tooltip}/>
        </div>
    );
};

export default TimelineBarChart;
